/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect, useRef } from "react";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import {
  Button,
  Card,
  CardContent,
  CardTitle,
  Combobox,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { showToast } from "utils/helper";
import {
  useGetCashCenters,
  useGetCustomers,
  useGetVaultCurrencies,
  useGetVaultDenominations,
} from "utils/apis/lookups.api";
import KeyInCashEntry from "./keyin-cash-entry";
import KeyInCashGrid from "./keyin-cash-grid";

function AddCashToVault({ addCashHandler }) {
  const [ddlCashCenters, setDdlCashCenters] = useState("");
  const [ddlCustomers, setDdlCustomers] = useState("");
  const [ddlCurrencies, setDdlCurrencies] = useState("");
  const [denominations, setDenominations] = useState([]);
  const [btnAddCashDisabled, setBtnAddCashDisabled] = useState(true);
  const [declaredValue, setDeclaredValue] = useState(0);
  const [txtDeclaredValueColor, setTxtDeclaredValueColor] = useState("");
  const [customerCash, setCustomerCash] = useState([]);

  const currencyDenominationsList = useRef([]);
  const { data: cashCenters } = useGetCashCenters();
  const { data: customers } = useGetCustomers();
  const { data: currencies } = useGetVaultCurrencies();
  const {
    data: denomData,
    mutate: getVaultDenominations,
    isError: isDenomError,
  } = useGetVaultDenominations();

  const onDdlCurrenciesChanged = async (shortName) => {
    currencyDenominationsList.current = [];
    setDdlCurrencies(shortName);

    getVaultDenominations(shortName);
  };

  useEffect(() => {
    if (denomData?.length && !isDenomError) {
      const denoms = denomData.map((d) => ({
        denomination: d.denomination,
        shape: d.shape,
        count: 0,
      }));

      setDenominations(denoms);
    }

    if (isDenomError) {
      showToast("error", "Unable to load denominations");
    }
  }, [denomData, isDenomError]);

  const updateTxtData = (rowIndex, columnID, value) => {
    setDenominations((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnID]: value,
          };
        }
        return row;
      })
    );
  };

  const addDenomination = (rowIndex, columnID, value) => {
    denominations.map((row, index) => {
      if (index === rowIndex) {
        if (columnID === "count") {
          const count = parseInt(value);
          const denom = row.denomination;
          const shape = row.shape;

          const amount = parseFloat(denom.split(" ")[1]) * parseFloat(count);

          const denominationFound = currencyDenominationsList.current.find(
            (dnm) => dnm.denomination === denom && dnm.shape === shape
          );

          if (denominationFound) {
            if (count === 0) {
              const itemToRemove = currencyDenominationsList.current.findIndex(
                (dl) => dl.denomination === denom && dl.shape === shape
              );
              currencyDenominationsList.current.splice(itemToRemove, 1);
            } else {
              denominationFound.count = count;
              denominationFound.amount = amount;

              const itemToRemove = currencyDenominationsList.current.findIndex(
                (dl) => dl.denomination === denom && dl.shape === shape
              );
              currencyDenominationsList.current.splice(
                itemToRemove,
                1,
                denominationFound
              );
            }
          } else if (count > 0) {
            const denomObj = {
              denomination: denom,
              count,
              amount,
              shape,
            };

            currencyDenominationsList.current.push(denomObj);
          }

          const denomsList = [];

          currencyDenominationsList.current.forEach((dnm) => {
            denomsList.push(dnm);
          });

          setCustomerCash(denomsList);

          validateDeclaredValue();
        }
      }
      return row;
    });
  };

  const validateDeclaredValue = () => {
    const selectedCurrency = ddlCurrencies;

    const selectedCurrencyDenoms = currencyDenominationsList.current.filter(
      (dnm) => dnm.denomination.split(" ")[0] === selectedCurrency
    );

    let currentTotal = 0;

    selectedCurrencyDenoms.forEach((scd) => {
      const multiple = parseFloat(scd.denomination.split(" ")[1]);
      const denomCount = parseInt(scd.count);
      const total = multiple * denomCount;
      currentTotal += total;
    });

    if (parseInt(declaredValue) === parseInt(currentTotal)) {
      setBtnAddCashDisabled(false);
      setTxtDeclaredValueColor("text-success");
    } else {
      setBtnAddCashDisabled(true);
      setTxtDeclaredValueColor("text-error");
    }
  };

  const onBtnAddCashClicked = async () => {
    if (!ddlCashCenters) {
      showToast("error", "Please select cash center");
      return;
    }

    if (!ddlCustomers) {
      showToast("error", "Please select customer");
      return;
    }

    if (!ddlCurrencies) {
      showToast("error", "Please select currency");
      return;
    }

    const cashObj = {
      cashCenterId: ddlCashCenters,
      customerId: ddlCustomers,
      customerCash,
    };

    addCashHandler(cashObj);
  };

  return (
    <div className="flex gap-3">
      <div className="flex-1">
        <Card>
          <CardTitle className="text-center text-lg py-3 text-secondary bg-secondary/25">
            Cash Entry
          </CardTitle>
          <CardContent className="p-3">
            <Select
              onValueChange={setDdlCashCenters}
              defaultValue={ddlCashCenters}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Cash Center" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {cashCenters.map((item) => (
                    <SelectItem
                      key={item.cashCenterId}
                      value={item.cashCenterId}
                    >
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <div className="border-b my-3 bg-accent" />
            <Combobox
              placeholder="Select Customer"
              data={customers?.map((item) => ({
                label: item.name,
                value: item.customerId,
              }))}
              value={ddlCustomers}
              setValue={setDdlCustomers}
            />

            <div className="border-b my-3 bg-accent" />
            <Combobox
              placeholder="Select Currency"
              data={currencies?.map((item) => ({
                label: item.name,
                value: item.shortName,
              }))}
              value={ddlCurrencies}
              setValue={onDdlCurrenciesChanged}
            />

            <div className="border-b my-3 bg-accent" />

            <NumericTextBox
              step={0}
              spinners={false}
              placeholder="Declared Value"
              className={`w-100 h-10 ${txtDeclaredValueColor}`}
              value={declaredValue}
              onChange={(e) => setDeclaredValue(e.target.value)}
            />

            <div className="border-b my-3  bg-accent" />

            <KeyInCashEntry
              data={denominations}
              updateTxtData={updateTxtData}
              addKeyInDenomination={addDenomination}
            />
          </CardContent>
        </Card>
      </div>
      <div className="flex-1">
        <Card className="text-center">
          <CardTitle className="text-center text-lg py-3 text-secondary bg-secondary/25">
            Review
          </CardTitle>
          <CardContent className="p-3">
            <KeyInCashGrid data={customerCash} />
            <Button
              variant="secondary"
              className="w-full mt-2"
              disabled={btnAddCashDisabled}
              onClick={onBtnAddCashClicked}
            >
              Add Cash
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AddCashToVault;
